import { ApiMethodExecutingError } from '../service/Errors';
import { captureException } from '../service/ErrorsReporting/ErrorsReporting';
import { FeedFiltersModalShownView } from '../service/FeedFiltersHelpers/FeedFiltersHelpers';
import { NumberRangeInputValues } from '../typings/common';
import { FeedCategoryNode } from '../typings/feedCategories';
import {
    GdShowConditionAutobudgetPriority,
    GdSmartFilterTab,
    GdSmartFilterTargetFunnel
} from '../typings/graphql-grid';
import {
    EditGroupFilterItem,
    FeedCategory,
    FeedFilterCampaign,
    FeedFilterSchema
} from '../typings/groupsEditState';

export const OPEN_GROUP_FILTER_INSPECTOR = 'OPEN_GROUP_FILTER_INSPECTOR';
export type OPEN_GROUP_FILTER_INSPECTOR = typeof OPEN_GROUP_FILTER_INSPECTOR;

export interface OpenGroupFilterInspectorPayload {
    groupId: string;
    feedId: string;
    filter?: EditGroupFilterItem;
    campaign?: FeedFilterCampaign;
}

export interface OpenGroupFilterInspectorAction {
    type: OPEN_GROUP_FILTER_INSPECTOR;
    payload: OpenGroupFilterInspectorPayload;
}

export function openGroupFilterInspector(
    payload: OpenGroupFilterInspectorPayload
): OpenGroupFilterInspectorAction {
    return {
        type: OPEN_GROUP_FILTER_INSPECTOR,
        payload
    };
}

export const CLOSE_GROUP_FILTER_INSPECTOR = 'CLOSE_GROUP_FILTER_INSPECTOR';
export type CLOSE_GROUP_FILTER_INSPECTOR = typeof CLOSE_GROUP_FILTER_INSPECTOR;

export interface CloseGroupFilterInspectorAction {
    type: CLOSE_GROUP_FILTER_INSPECTOR;
}

export function closeGroupFilterInspector(): CloseGroupFilterInspectorAction {
    return {
        type: CLOSE_GROUP_FILTER_INSPECTOR
    };
}

export const SET_FILTER_FEED_CATEGORIES = 'SET_FILTER_FEED_CATEGORIES';
export type SET_FILTER_FEED_CATEGORIES = typeof SET_FILTER_FEED_CATEGORIES;

export interface SetFilterFeedCategoriesAction {
    type: SET_FILTER_FEED_CATEGORIES;
    categories: Array<FeedCategory>;
}

export function setFilterFeedCategories(
    categories: Array<FeedCategory>
): SetFilterFeedCategoriesAction {
    return {
        type: SET_FILTER_FEED_CATEGORIES,
        categories
    };
}

export const UPDATE_FEED_FILTER_SCHEMA = 'UPDATE_FEED_FILTER_SCHEMA';
export type UPDATE_FEED_FILTER_SCHEMA = typeof UPDATE_FEED_FILTER_SCHEMA;

export interface UpdateFeedFilterSchemaAction {
    type: UPDATE_FEED_FILTER_SCHEMA;
    schema: FeedFilterSchema;
}

export function updateFeedFilterSchema(
    schema: FeedFilterSchema
): UpdateFeedFilterSchemaAction {
    return {
        type: UPDATE_FEED_FILTER_SCHEMA,
        schema
    };
}

export const SELECT_AUTOBUDGET_PRIORITY_TYPE =
    'SELECT_AUTOBUDGET_PRIORITY_TYPE';
export type SELECT_AUTOBUDGET_PRIORITY_TYPE = typeof SELECT_AUTOBUDGET_PRIORITY_TYPE;

export interface SelectAutobudgetPriorityTypeAction {
    type: SELECT_AUTOBUDGET_PRIORITY_TYPE;
    priority: GdShowConditionAutobudgetPriority;
}

export function selectAutobudgetPriorityType(
    priority: GdShowConditionAutobudgetPriority
): SelectAutobudgetPriorityTypeAction {
    return {
        type: SELECT_AUTOBUDGET_PRIORITY_TYPE,
        priority
    };
}

export const UPDATE_AUTOBUDGET_PRICE = 'UPDATE_AUTOBUDGET_PRICE';
export type UPDATE_AUTOBUDGET_PRICE = typeof UPDATE_AUTOBUDGET_PRICE;

export interface UpdateAutobudgetPriceAction {
    type: UPDATE_AUTOBUDGET_PRICE;
    cpaOrCpc: keyof Pick<EditGroupFilterItem, 'priceCpa' | 'priceCpc'>;
    value?: number;
}

export function updateAutobudgetPrice(
    cpaOrCpc: keyof Pick<EditGroupFilterItem, 'priceCpa' | 'priceCpc'>,
    value?: number
): UpdateAutobudgetPriceAction {
    return {
        type: UPDATE_AUTOBUDGET_PRICE,
        cpaOrCpc,
        value
    };
}

export const UPDATE_SMART_FILTER_NAME = 'UPDATE_SMART_FILTER_NAME';
export type UPDATE_SMART_FILTER_NAME = typeof UPDATE_SMART_FILTER_NAME;

export interface UpdateSmartFilterNameAction {
    type: UPDATE_SMART_FILTER_NAME;
    value: string;
}

export function updateSmartFilterName(
    value: string
): UpdateSmartFilterNameAction {
    return {
        type: UPDATE_SMART_FILTER_NAME,
        value
    };
}

export const UPDATE_SMART_FILTER_PRICE_RANGE =
    'UPDATE_SMART_FILTER_PRICE_RANGE';
export type UPDATE_SMART_FILTER_PRICE_RANGE = typeof UPDATE_SMART_FILTER_PRICE_RANGE;

export interface UpdateSmartFilterPriceRangeAction {
    type: UPDATE_SMART_FILTER_PRICE_RANGE;
    priceRange: NumberRangeInputValues;
}

export function updateSmartFilterPriceRange(
    priceRange: NumberRangeInputValues
): UpdateSmartFilterPriceRangeAction {
    return {
        type: UPDATE_SMART_FILTER_PRICE_RANGE,
        priceRange
    };
}

export const UPDATE_SMART_FILTER_VENDORS = 'UPDATE_SMART_FILTER_VENDORS';
export type UPDATE_SMART_FILTER_VENDORS = typeof UPDATE_SMART_FILTER_VENDORS;

export interface UpdateSmartFilterVendorsAction {
    type: UPDATE_SMART_FILTER_VENDORS;
    vendors: string;
}

export function updateSmartFilterVendors(
    vendors: string
): UpdateSmartFilterVendorsAction {
    return {
        type: UPDATE_SMART_FILTER_VENDORS,
        vendors
    };
}

export const TOGGLE_SMART_FILTER_AVAILABLE = 'TOGGLE_SMART_FILTER_AVAILABLE';
export type TOGGLE_SMART_FILTER_AVAILABLE = typeof TOGGLE_SMART_FILTER_AVAILABLE;

export interface ToggleSmartFilterAvailableAction {
    type: TOGGLE_SMART_FILTER_AVAILABLE;
}

export function toggleSmartFilterAvailable(): ToggleSmartFilterAvailableAction {
    return {
        type: TOGGLE_SMART_FILTER_AVAILABLE
    };
}

export const ADD_SMART_FILTER = 'ADD_SMART_FILTER';
export type ADD_SMART_FILTER = typeof ADD_SMART_FILTER;

export interface AddSmartFilterAction {
    type: ADD_SMART_FILTER;
    view: FeedFiltersModalShownView;
}

export function addSmartFilter(
    view: FeedFiltersModalShownView
): AddSmartFilterAction {
    return {
        type: ADD_SMART_FILTER,
        view
    };
}

export const UPDATE_SMART_FILTER = 'UPDATE_SMART_FILTER';
export type UPDATE_SMART_FILTER = typeof UPDATE_SMART_FILTER;

export interface UpdateSmartFilterAction {
    type: UPDATE_SMART_FILTER;
    view: FeedFiltersModalShownView;
}

export function updateSmartFilter(
    view: FeedFiltersModalShownView
): UpdateSmartFilterAction {
    return {
        type: UPDATE_SMART_FILTER,
        view
    };
}

export const UPDATE_SMART_FILTER_SUCCESS = 'UPDATE_SMART_FILTER_SUCCESS';
export type UPDATE_SMART_FILTER_SUCCESS = typeof UPDATE_SMART_FILTER_SUCCESS;

export interface UpdateSmartFilterSuccessAction {
    type: UPDATE_SMART_FILTER_SUCCESS;
    view: FeedFiltersModalShownView;
    groupId: string;
}

export function updateSmartFilterSuccess(
    view: FeedFiltersModalShownView,
    groupId: string
): UpdateSmartFilterSuccessAction {
    return {
        type: UPDATE_SMART_FILTER_SUCCESS,
        view,
        groupId
    };
}

export const UPDATE_SMART_FILTER_AUDITORY = 'UPDATE_SMART_FILTER_AUDITORY';
export type UPDATE_SMART_FILTER_AUDITORY = typeof UPDATE_SMART_FILTER_AUDITORY;

export interface UpdateSmartFilterAuditoryAction {
    type: UPDATE_SMART_FILTER_AUDITORY;
    value: GdSmartFilterTargetFunnel;
}

export function updateSmartFilterAuditory(
    value: GdSmartFilterTargetFunnel
): UpdateSmartFilterAuditoryAction {
    return {
        type: UPDATE_SMART_FILTER_AUDITORY,
        value
    };
}

export const UPDATE_SMART_FILTER_CATEGORIES = 'UPDATE_SMART_FILTER_CATEGORIES';
export type UPDATE_SMART_FILTER_CATEGORIES = typeof UPDATE_SMART_FILTER_CATEGORIES;

export interface UpdateSmartFilterCategoriesAction {
    type: UPDATE_SMART_FILTER_CATEGORIES;
    categories: Array<FeedCategoryNode>;
}

export function updateSmartFilterCategories(
    categories: Array<FeedCategoryNode>
): UpdateSmartFilterCategoriesAction {
    return {
        type: UPDATE_SMART_FILTER_CATEGORIES,
        categories
    };
}

export const CHANGE_SMART_FILTER_TAB = 'CHANGE_SMART_FILTER_TAB';
export type CHANGE_SMART_FILTER_TAB = typeof CHANGE_SMART_FILTER_TAB;

export interface ChangeSmartFilterTabAction {
    type: CHANGE_SMART_FILTER_TAB;
    tab: GdSmartFilterTab;
}

export function changeSmartFilterTab(
    tab: GdSmartFilterTab
): ChangeSmartFilterTabAction {
    return {
        type: CHANGE_SMART_FILTER_TAB,
        tab
    };
}

export const SET_SMART_FILTER_VALIDATION_ERRORS =
    'SET_SMART_FILTER_VALIDATION_ERRORS';
export type SET_SMART_FILTER_VALIDATION_ERRORS = typeof SET_SMART_FILTER_VALIDATION_ERRORS;

export interface SetSmartFilterValidationErrorsAction {
    type: SET_SMART_FILTER_VALIDATION_ERRORS;
    errors: Record<string, Object>;
}

export function setSmartFilterValidationErrors(
    errors: Record<string, Object>
): SetSmartFilterValidationErrorsAction {
    return {
        type: SET_SMART_FILTER_VALIDATION_ERRORS,
        errors
    };
}

export const UPDATE_FILTERS_ERROR = 'UPDATE_FILTERS_ERROR';
export type UPDATE_FILTERS_ERROR = typeof UPDATE_FILTERS_ERROR;

export interface UpdateFiltersErrorAction {
    type: UPDATE_FILTERS_ERROR;
}

export function updateFiltersError(
    error: ApiMethodExecutingError | string
): UpdateFiltersErrorAction {
    if (error instanceof ApiMethodExecutingError) {
        captureException(error.getNativeError());

        return {
            type: UPDATE_FILTERS_ERROR
        };
    } else {
        captureException(error);
        throw error;
    }
}

export type GroupFilterInspectorAction =
    | OpenGroupFilterInspectorAction
    | CloseGroupFilterInspectorAction
    | SetFilterFeedCategoriesAction
    | UpdateFeedFilterSchemaAction
    | SelectAutobudgetPriorityTypeAction
    | UpdateAutobudgetPriceAction
    | UpdateSmartFilterNameAction
    | AddSmartFilterAction
    | UpdateSmartFilterAction
    | UpdateSmartFilterSuccessAction
    | UpdateSmartFilterAuditoryAction
    | UpdateSmartFilterCategoriesAction
    | UpdateSmartFilterPriceRangeAction
    | UpdateSmartFilterVendorsAction
    | ToggleSmartFilterAvailableAction
    | ChangeSmartFilterTabAction
    | SetSmartFilterValidationErrorsAction
    | UpdateFiltersErrorAction;
